import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { measureBeans, METHOD_VERSION, type MeasureResult } from "../lib/beanMeasure";
import type { Coffee, BeanMeasurement } from "../types";

type Phase = "idle" | "processing" | "review" | "error";

const mm = (v: number | null | undefined, d = 2) => (v == null ? "—" : `${v.toFixed(d)} mm`);
const scr = (v: number | null | undefined) => (v == null ? "—" : `#${v.toFixed(1)}`);

export function BeanSizeSection({ coffee, onCoffeeChange }: {
  coffee: Coffee;
  onCoffeeChange: (c: Coffee) => void;
}) {
  const [rows, setRows] = useState<BeanMeasurement[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<MeasureResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.beanMeasurements.list(coffee.id).then(setRows).catch(() => {});
  }, [coffee.id]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setPhase("processing"); setErr(null); setResult(null); setStage("starting"); setElapsed(0);
    const t0 = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 500);
    try {
      const r = await measureBeans(file, setStage);
      setResult(r);
      if (r.ok) setPhase("review");
      else { setErr(r.error || "Measurement failed."); setPhase(r.annotated ? "review" : "error"); }
    } catch (e2) {
      setErr((e2 as Error).message || "Measurement failed.");
      setPhase("error");
    } finally {
      window.clearInterval(timer);
    }
  };

  const onSave = async () => {
    if (!result?.ok) return;
    setSaving(true);
    try {
      const a = result.aggregate;
      const { measurement, coffee: updated } = await api.beanMeasurements.create(coffee.id, {
        n: a.n,
        median_len_mm: a.medianLen,
        median_width_mm: a.medianWidth,
        median_aspect: a.medianAspect,
        median_screen: a.medianScreen,
        spread_width_mm: a.spreadWidth,
        scale_mm_px: result.scaleMmPx,
        coin_mm: result.coinMm,
        selftest_circle_mm: result.selftestCircleMm,
        per_bean: result.beans,
        method_version: METHOD_VERSION,
      });
      setRows((cur) => [measurement, ...cur]);
      if (updated) onCoffeeChange({ ...coffee, bean_size: updated.bean_size });
      setPhase("idle"); setResult(null);
    } catch (e) {
      setErr((e as Error).message || "Save failed.");
    } finally { setSaving(false); }
  };

  const reset = () => { setPhase("idle"); setResult(null); setErr(null); };

  return (
    <section>
      <div className="eyebrow mb-3 flex items-center gap-3">
        <span>Bean size · {rows.length}</span>
        {coffee.bean_size != null ? (
          <span className="text-[10px] tracking-widest text-paper-500">LATEST {scr(coffee.bean_size)} SCREEN</span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={phase === "processing"}>
          {phase === "processing" ? "Measuring…" : "Measure bean size"}
        </button>
        <a className="btn-ghost" href="/calibration-sheet.html" target="_blank" rel="noreferrer">Print calibration sheet</a>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
      </div>

      <p className="text-xs text-paper-500 max-w-xl mb-4">
        Lay 10–20 beans on the sheet's bean zone (single layer, spaced) with the 10 ฿ coin on its circle,
        shoot straight top-down in even light. Measured on-device — the photo never leaves your phone.
        Bean size is a flavor/roast descriptor, not a grind setting.
      </p>

      {phase === "processing" ? (
        <p className="text-sm text-paper-500">
          {stage || "starting"}… <span className="text-paper-400">{elapsed}s</span>
          {stage === "loading vision engine" && elapsed > 8 ? " — first load pulls the ~10 MB engine; if this sticks past ~40s it'll error out" : ""}
        </p>
      ) : null}

      {phase === "error" ? (
        <div className="border border-paper-200 bg-paper-50 p-4 text-sm">
          <p className="text-rose-700">{err}</p>
          <button className="btn-ghost mt-3" onClick={reset}>Try again</button>
        </div>
      ) : null}

      {phase === "review" && result ? (
        <div className="border border-paper-200 bg-paper-50 p-4 space-y-4">
          {result.annotated ? (
            <img src={result.annotated} alt="Detected beans" className="max-w-full rounded border border-paper-200" />
          ) : null}

          {err ? <p className="text-rose-700 text-sm">{err}</p> : null}
          {result.warnings.map((w, i) => <p key={i} className="text-amber-700 text-xs">⚠ {w}</p>)}

          {result.ok ? (
            <>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                <Stat label={`Median width (n=${result.aggregate.n})`} value={mm(result.aggregate.medianWidth)} />
                <Stat label="Screen size" value={scr(result.aggregate.medianScreen)} />
                <Stat label="Median length" value={mm(result.aggregate.medianLen)} />
                <Stat label="Aspect (shape)" value={result.aggregate.medianAspect?.toFixed(2) ?? "—"} />
                <Stat label="Width spread (IQR)" value={mm(result.aggregate.spreadWidth)} />
                <Stat
                  label="Scale check · box→261"
                  value={result.selftestCircleMm == null
                    ? "—"
                    : `${result.selftestCircleMm.toFixed(0)} mm ${Math.abs(result.selftestCircleMm - 261) / 261 <= 0.03 ? "✓" : "⚠"}`}
                />
              </dl>
              <div className="flex gap-2">
                <button className="btn-primary" onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save measurement"}</button>
                <button className="btn-ghost" onClick={() => fileRef.current?.click()}>Retake</button>
                <button className="btn-ghost" onClick={reset}>Cancel</button>
              </div>
            </>
          ) : (
            <button className="btn-ghost" onClick={() => fileRef.current?.click()}>Retake</button>
          )}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <ul className="border-t border-paper-200 text-sm mt-4">
          {rows.map((r) => (
            <li key={r.id} className="py-2 border-b border-paper-200 flex justify-between gap-4">
              <span>{new Date(r.measured_at + "Z").toLocaleDateString()}</span>
              <span className="text-paper-600">
                {scr(r.median_screen)} · {mm(r.median_width_mm)} wide · n={r.n}
              </span>
            </li>
          ))}
        </ul>
      ) : phase === "idle" ? (
        <p className="text-sm text-paper-500">No measurements yet.</p>
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-paper-500 text-xs">{label}</div>
      <div className="text-ink text-base">{value}</div>
    </div>
  );
}
